import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, CheckCircle2, Loader2, UserPlus } from "lucide-react";
import Sidebar from "../Sidebar";
import DoctorSearch from "../Support/DoctorSearch";
import { customFetch } from "../../utils/Auth";
import { environment } from "../../config/environment";
import { findWorkflowMatch } from "../WorkflowsVisualization/buildTable";
import {
  buildRequest,
  emptyAnswers,
  getMinimumDate,
  getSteps,
  integratedAccount,
  isAnswerComplete,
  isUnavailableIntegratedEhr,
  options,
  questions,
  updateAnswer,
} from "./flow";

const controlClass =
  "w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-ktx-purple disabled:bg-gray-50";

export const OPTIONS_TIMEOUT_MS = 15000;
export const NOTE_GENERATION_TIMEOUT_MS = 90000;
const WORKFLOW_PATIENT_TYPES = {
  new: "NEW",
  fu: "FOLLOWUP",
  telemed: "TELEMED",
};

function loadData(path, label, parseData) {
  const controller = new AbortController();
  let rejectPending;
  // Bound the entire operation, including customFetch's session lookup and
  // response.json(). Aborting fetch alone cannot stop a stalled session lookup.
  const pending = new Promise((_, reject) => {
    rejectPending = reject;
  });
  const timeout = setTimeout(() => {
    rejectPending(
      new Error(
        `Loading ${label} timed out. Check your connection and try again.`,
      ),
    );
    controller.abort();
  }, OPTIONS_TIMEOUT_MS);
  const request = (async () => {
    try {
      const response = await customFetch(`${environment.apiUrl}${path}`, {
        signal: controller.signal,
      });
      if (!response.ok)
        throw new Error(
          `Unable to load ${label} (HTTP ${response.status}). Please try again.`,
        );
      const data = await response.json();
      return parseData(data);
    } catch (error) {
      if (controller.signal.aborted) throw error;
      const message = error?.message || "";
      throw new Error(
        message.startsWith("Unable to load") ||
          message.startsWith("The server returned")
          ? message
          : `Unable to load ${label}. Check your connection and try again.`,
      );
    }
  })();
  return {
    promise: Promise.race([request, pending]).finally(() =>
      clearTimeout(timeout),
    ),
    cancel() {
      clearTimeout(timeout);
      rejectPending(new Error("Lookup cancelled"));
      controller.abort();
    },
  };
}

function loadOptions(path, label) {
  return loadData(path, label, (data) => {
    if (!Array.isArray(data))
      throw new Error(
        `The server returned an invalid ${label} list. Please try again.`,
      );
    return data;
  });
}

function loadWorkflows() {
  return loadData("/workflows/load-from-s3", "workflows", (result) => {
    const workflows = result?.success && result?.data?.workflows;
    if (!Array.isArray(workflows))
      throw new Error(
        "The server returned invalid workflows. Please try again.",
      );
    return workflows;
  });
}

export default function AddTestPatient({ setAuth, setUserOrg }) {
  const [answers, setAnswers] = useState({ ...emptyAnswers });
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(true);
  const [specialtiesLoading, setSpecialtiesLoading] = useState(true);
  const [doctorsError, setDoctorsError] = useState("");
  const [specialtiesError, setSpecialtiesError] = useState("");
  const [reloadDoctors, setReloadDoctors] = useState(0);
  const [reloadSpecialties, setReloadSpecialties] = useState(0);
  const [workflows, setWorkflows] = useState(null);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [workflowsError, setWorkflowsError] = useState("");
  const [reloadWorkflows, setReloadWorkflows] = useState(0);
  const [generatingNote, setGeneratingNote] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [generationAttempt, setGenerationAttempt] = useState(0);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const submitting = useRef(false);

  useEffect(() => {
    let active = true;
    setDoctorsLoading(true);
    setDoctorsError("");
    const request = loadOptions("/doctors/all-doctors", "doctors");
    request.promise
      .then((data) => {
        if (active) setDoctors(data);
      })
      .catch((error) => {
        if (active) setDoctorsError(error.message);
      })
      .finally(() => {
        if (active) setDoctorsLoading(false);
      });
    return () => {
      active = false;
      request.cancel();
    };
  }, [reloadDoctors]);

  useEffect(() => {
    let active = true;
    setSpecialtiesLoading(true);
    setSpecialtiesError("");
    // Same endpoint and alphabetical ordering as Register Doctors.
    const request = loadOptions(
      "/doctors/specialties-for-update",
      "specialties",
    );
    request.promise
      .then((data) => {
        if (active)
          setSpecialties(
            [
              ...new Set(data.map((item) => item.specialty).filter(Boolean)),
            ].sort((a, b) => a.localeCompare(b)),
          );
      })
      .catch((error) => {
        if (active) setSpecialtiesError(error.message);
      })
      .finally(() => {
        if (active) setSpecialtiesLoading(false);
      });
    return () => {
      active = false;
      request.cancel();
    };
  }, [reloadSpecialties]);

  useEffect(() => {
    const needsWorkflows =
      answers.purpose === "onboarding" ||
      (answers.purpose === "sales" && answers.demoMode === "standalone");
    if (!needsWorkflows || workflows !== null) return undefined;
    let active = true;
    setWorkflowsLoading(true);
    setWorkflowsError("");
    const request = loadWorkflows();
    request.promise
      .then((data) => {
        if (active) setWorkflows(data);
      })
      .catch((error) => {
        if (active) setWorkflowsError(error.message);
      })
      .finally(() => {
        if (active) setWorkflowsLoading(false);
      });
    return () => {
      active = false;
      request.cancel();
    };
  }, [answers.demoMode, answers.purpose, reloadWorkflows, workflows]);

  const workflowMatch = useMemo(() => {
    const patientType = WORKFLOW_PATIENT_TYPES[answers.patientType];
    if (!answers.doctor || !patientType || !Array.isArray(workflows))
      return null;
    return findWorkflowMatch(workflows, {
      specialty: answers.doctor.specialty || null,
      providerId: answers.doctor.user_id,
      providerName: answers.doctor.user_name,
      patientType,
      flags: [],
      allowUnlistedProvider: Boolean(answers.doctor.specialty),
    });
  }, [answers.doctor, answers.patientType, workflows]);
  const noteTemplate =
    workflowMatch?.resources?.find(
      (resource) => resource.key === "soap_note_template",
    )?.id || null;
  const conversationPrompt =
    workflowMatch?.resources?.find(
      (resource) => resource.key === "conversation_prompt",
    )?.id || null;
  const standaloneWorkflowMatch = useMemo(() => {
    if (
      answers.purpose !== "sales" ||
      !answers.demoMode ||
      !answers.doctor ||
      !answers.specialty ||
      !Array.isArray(workflows)
    )
      return null;
    return findWorkflowMatch(workflows, {
      specialty: answers.specialty,
      providerId: answers.doctor.user_id,
      providerName: answers.doctor.user_name,
      patientType: WORKFLOW_PATIENT_TYPES.new,
      flags: [],
      allowUnlistedProvider: true,
    });
  }, [
    answers.demoMode,
    answers.doctor,
    answers.purpose,
    answers.specialty,
    workflows,
  ]);
  const standaloneNoteTemplate =
    standaloneWorkflowMatch?.resources?.find(
      (resource) => resource.key === "soap_note_template",
    )?.id || null;

  useEffect(() => {
    if (answers.encounter !== "review") {
      setGeneratingNote(false);
      setGenerationError("");
      return undefined;
    }
    if (
      workflowsLoading ||
      workflowsError ||
      !noteTemplate ||
      !conversationPrompt
    )
      return undefined;

    let active = true;
    const controller = new AbortController();
    let rejectTimeout;
    const timeoutPromise = new Promise((_, reject) => {
      rejectTimeout = reject;
    });
    const timeout = setTimeout(() => {
      rejectTimeout(
        new Error("Synthetic note generation timed out. Please try again."),
      );
      controller.abort();
    }, NOTE_GENERATION_TIMEOUT_MS);

    setGeneratingNote(true);
    setGenerationError("");
    setAnswers((current) =>
      current.encounter === "review"
        ? {
            ...current,
            syntheticNote: "",
            noteApproved: false,
            soapNoteTemplate: noteTemplate,
            conversationPrompt,
            facesheetCase: null,
          }
        : current,
    );

    const request = (async () => {
      const response = await customFetch(
        `${environment.apiUrl}/test-patients/generate-note`,
        {
          method: "POST",
          signal: controller.signal,
          body: JSON.stringify({
            patientType: answers.patientType,
            userId: answers.doctor.user_id,
            soapNoteTemplate: noteTemplate,
            conversationPrompt,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Unable to generate the synthetic note.");
      if (typeof data.note !== "string" || !data.note.trim())
        throw new Error("Anthropic returned an empty synthetic note.");
      if (!data.facesheetCase || typeof data.facesheetCase !== "object")
        throw new Error(
          "The server did not return the patient case for this note. Please try again.",
        );
      return data;
    })();

    Promise.race([request, timeoutPromise])
      .then((data) => {
        if (!active) return;
        setAnswers((current) =>
          current.encounter === "review"
            ? {
                ...current,
                syntheticNote: data.note,
                facesheetCase: data.facesheetCase,
                noteApproved: false,
                soapNoteTemplate: data.soapNoteTemplate || noteTemplate,
                conversationPrompt:
                  data.conversationPrompt || conversationPrompt,
              }
            : current,
        );
      })
      .catch((error) => {
        if (active)
          setGenerationError(
            error.message ||
              "Unable to generate the synthetic note. Please try again.",
          );
      })
      .finally(() => {
        clearTimeout(timeout);
        if (active) setGeneratingNote(false);
      });

    return () => {
      active = false;
      clearTimeout(timeout);
      rejectTimeout(new Error("Generation cancelled"));
      controller.abort();
    };
  }, [
    answers.doctor?.user_id,
    answers.encounter,
    answers.patientType,
    conversationPrompt,
    generationAttempt,
    noteTemplate,
    workflowsError,
    workflowsLoading,
  ]);

  const steps = getSteps(answers);
  const firstIncomplete = steps.findIndex(
    (field) => !isAnswerComplete(field, answers[field], answers),
  );
  const complete = steps.includes("date") && firstIncomplete === -1;
  const visibleSteps =
    firstIncomplete === -1 ? steps : steps.slice(0, firstIncomplete + 1);
  const completedCount =
    firstIncomplete === -1 ? steps.length : firstIncomplete;
  const integrated =
    answers.purpose === "sales" && answers.demoMode === "integrated";
  const destinationEmail = integrated
    ? integratedAccount.email
    : answers.doctor?.user_email || answers.doctor?.user_name;
  const destinationSpecialty = integrated
    ? integratedAccount.specialty
    : answers.specialty;

  function changeAnswer(field, value) {
    if (submitting.current) return;
    setAnswers((current) => updateAnswer(current, field, value));
    setError("");
  }

  async function submitPatient() {
    if (!complete || submitting.current || created) return;
    submitting.current = true;
    setCreating(true);
    setError("");
    try {
      const request = buildRequest(answers);
      const response = await customFetch(
        `${environment.apiUrl}/test-patients`,
        {
          method: "POST",
          body: JSON.stringify(request),
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Unable to add the patient.");
      if (!data.patient?.patient_id || !data.patient?.description)
        throw new Error(
          "The server did not confirm the patient was added. Check the account before trying again.",
        );
      if (
        integrated &&
        (!data.followUpPatient?.patient_id ||
          !data.followUpPatient?.description)
      )
        throw new Error(
          "The server did not confirm both patients were added. Check jocelyn@knowtex.ai before trying again.",
        );
      setCreated(data);
    } catch (err) {
      setError(
        err.message ||
          "Unable to confirm the patient was added. Check the account before trying again.",
      );
    } finally {
      submitting.current = false;
      setCreating(false);
    }
  }

  function createPatient(event) {
    event.preventDefault();
    return submitPatient();
  }

  function reset() {
    setAnswers({ ...emptyAnswers });
    setCreated(null);
    setError("");
    setGenerationError("");
    setGenerationAttempt(0);
  }

  function regenerateNote() {
    setAnswers((current) => ({
      ...current,
      syntheticNote: "",
      noteApproved: false,
    }));
    setGenerationAttempt((value) => value + 1);
  }

  function retryOptions(setReload) {
    return (
      <button
        type="button"
        className="mt-2 text-sm font-semibold text-ktx-purple underline"
        onClick={() => setReload((value) => value + 1)}
      >
        Try again
      </button>
    );
  }

  function renderField(field) {
    if (field === "doctor") {
      return answers.doctor ? (
        <div className="flex items-start justify-between gap-4 rounded-lg border border-purple-200 bg-purple-50 p-4">
          <div className="min-w-0 break-words">
            <p className="font-semibold text-gray-900">
              {answers.doctor.user_name || "No name set"}
            </p>
            <p className="text-sm text-gray-600">{answers.doctor.user_email}</p>
            <p className="mt-1 text-xs text-gray-500">
              {answers.doctor.user_id}
            </p>
          </div>
          <button
            type="button"
            className="text-sm font-semibold text-ktx-purple"
            onClick={() => changeAnswer("doctor", null)}
          >
            Change user
          </button>
        </div>
      ) : (
        <>
          <DoctorSearch
            allDoctors={doctors}
            isLoadingDoctors={doctorsLoading}
            doctorsLoadError={doctorsError}
            onDoctorSelected={(doctor) => changeAnswer("doctor", doctor)}
            label="Doctor name or email"
            description="Search by name or email, then select the account that should receive the test patient."
          />
          {doctorsError && retryOptions(setReloadDoctors)}
        </>
      );
    }
    if (field === "date") {
      return (
        <input
          id="answer-date"
          type="date"
          min={getMinimumDate(answers)}
          max="9999-12-31"
          required
          className={controlClass}
          value={answers.date}
          // Handle direct date entry as well as the native picker's change event.
          onInput={(event) => changeAnswer(field, event.currentTarget.value)}
          onChange={(event) => changeAnswer(field, event.target.value)}
        />
      );
    }
    const choices =
      field === "specialty"
        ? specialties.map((value) => [value, value])
        : options[field];
    return (
      <>
        <select
          id={`answer-${field}`}
          required
          className={controlClass}
          value={answers[field]}
          onChange={(event) => changeAnswer(field, event.target.value)}
          disabled={
            field === "specialty" &&
            (specialtiesLoading || Boolean(specialtiesError))
          }
        >
          <option value="">
            {field === "specialty" && specialtiesLoading
              ? "Loading specialties…"
              : "Select an option"}
          </option>
          {choices.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {field === "specialty" && specialtiesError && (
          <div role="alert" className="mt-2 text-sm text-red-700">
            {specialtiesError}
            <br />
            {retryOptions(setReloadSpecialties)}
          </div>
        )}
        {field === "specialty" &&
          !specialtiesLoading &&
          !specialtiesError &&
          specialties.length === 0 && (
            <p className="mt-2 text-sm text-gray-600">
              No specialties are available in Register Doctors yet.
            </p>
          )}
        {field === "specialty" && answers.specialty && workflowsLoading && (
          <p role="status" className="mt-2 text-sm text-gray-600">
            Looking up the New patient template…
          </p>
        )}
        {field === "specialty" && answers.specialty && workflowsError && (
          <div role="alert" className="mt-2 text-sm text-red-700">
            {workflowsError}
            <br />
            {retryOptions(setReloadWorkflows)}
          </div>
        )}
        {field === "specialty" &&
          answers.specialty &&
          !workflowsLoading &&
          !workflowsError &&
          standaloneNoteTemplate && (
            <p className="mt-2 break-words text-sm text-gray-600">
              New patient template:{" "}
              <strong className="font-semibold text-gray-900">
                {standaloneNoteTemplate}
              </strong>
              {standaloneWorkflowMatch?.isDefault && (
                <span> · {answers.specialty} fallback</span>
              )}
            </p>
          )}
        {field === "specialty" &&
          answers.specialty &&
          !workflowsLoading &&
          !workflowsError &&
          !standaloneNoteTemplate && (
            <p className="mt-2 text-sm text-amber-800">
              No New patient note template is configured for {answers.specialty}
              .
            </p>
          )}
        {field === "encounter" && answers.encounter === "review" && (
          <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-4">
            {(workflowsLoading || generatingNote) && (
              <p
                role="status"
                className="flex items-center gap-2 text-sm font-medium text-gray-700"
              >
                <Loader2
                  size={16}
                  className="animate-spin"
                  aria-hidden="true"
                />
                {workflowsLoading
                  ? "Waiting for the configured workflow…"
                  : "Generating a synthetic note with Claude Sonnet 5…"}
              </p>
            )}
            {!workflowsLoading &&
              !workflowsError &&
              (!noteTemplate || !conversationPrompt) && (
                <p role="alert" className="text-sm text-red-700">
                  A SOAP note template and conversation prompt must both be
                  configured before a Review patient can be created.
                </p>
              )}
            {generationError && (
              <div role="alert" className="text-sm text-red-700">
                {generationError}
                <button
                  type="button"
                  onClick={regenerateNote}
                  className="mt-2 block font-semibold text-ktx-purple underline"
                >
                  Try generation again
                </button>
              </div>
            )}
            {answers.syntheticNote && !generatingNote && (
              <div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Review the synthetic note
                  </h3>
                  {answers.noteApproved && (
                    <span
                      role="status"
                      className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800"
                    >
                      Approved
                    </span>
                  )}
                </div>
                <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-white p-4 font-sans text-sm leading-6 text-gray-800">
                  {answers.syntheticNote}
                </pre>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={answers.noteApproved}
                    onClick={() =>
                      setAnswers((current) => ({
                        ...current,
                        noteApproved: true,
                      }))
                    }
                    className="rounded-lg bg-ktx-purple px-4 py-2 text-sm font-semibold text-white disabled:bg-green-600"
                  >
                    {answers.noteApproved ? "Note approved" : "Approve note"}
                  </button>
                  <button
                    type="button"
                    onClick={regenerateNote}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Regenerate note
                  </button>
                </div>
                {!answers.noteApproved && (
                  <p className="mt-3 text-xs text-gray-600">
                    Approve this note to continue to the date question.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        {field === "patientType" && answers.patientType && workflowsLoading && (
          <p role="status" className="mt-2 text-sm text-gray-600">
            Looking up the configured template…
          </p>
        )}
        {field === "patientType" && answers.patientType && workflowsError && (
          <div role="alert" className="mt-2 text-sm text-red-700">
            {workflowsError}
            <br />
            {retryOptions(setReloadWorkflows)}
          </div>
        )}
        {field === "patientType" &&
          answers.patientType &&
          !workflowsLoading &&
          !workflowsError &&
          noteTemplate && (
            <p className="mt-2 break-words text-sm text-gray-600">
              Configured template:{" "}
              <strong className="font-semibold text-gray-900">
                {noteTemplate}
              </strong>
              {workflowMatch?.specialty && (
                <span>
                  {" "}
                  · {workflowMatch.specialty}
                  {workflowMatch.isDefault ? " fallback" : ""}
                </span>
              )}
            </p>
          )}
        {field === "patientType" &&
          answers.patientType &&
          !workflowsLoading &&
          !workflowsError &&
          conversationPrompt && (
            <p className="mt-1 break-words text-sm text-gray-600">
              Conversation prompt:{" "}
              <strong className="font-semibold text-gray-900">
                {conversationPrompt}
              </strong>
            </p>
          )}
        {field === "patientType" &&
          answers.patientType &&
          !workflowsLoading &&
          !workflowsError &&
          !noteTemplate && (
            <p className="mt-2 text-sm text-amber-800">
              No note template is configured for this user and patient type.
            </p>
          )}
        {field === "patientType" &&
          answers.patientType &&
          !workflowsLoading &&
          !workflowsError &&
          !conversationPrompt && (
            <p className="mt-1 text-sm text-amber-800">
              No conversation prompt is configured for this user and patient
              type.
            </p>
          )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      <main
        style={{ marginLeft: "var(--sidebar-width, 250px)" }}
        className="px-5 py-10 md:px-10"
      >
        <div className="mx-auto max-w-3xl">
          <div className="mb-8 flex items-start gap-4">
            <div className="rounded-xl bg-purple-100 p-3 text-ktx-purple">
              <UserPlus size={26} aria-hidden="true" />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
                Engineering
              </p>
              <h1 className="text-3xl font-bold text-gray-900">
                Add Test Patient
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Build a test encounter for an onboarding call or sales demo, one
                answer at a time.
              </p>
            </div>
          </div>

          {created ? (
            <section
              role="status"
              className="rounded-xl border border-green-200 bg-white p-7 shadow-sm"
            >
              <CheckCircle2
                className="mb-4 text-green-600"
                size={32}
                aria-hidden="true"
              />
              <h2 className="text-xl font-semibold text-gray-900">
                {created.followUpPatient
                  ? "Both patients added successfully"
                  : "Patient added successfully"}
              </h2>
              <p className="mt-2 text-gray-600">
                <strong>{created.patient.description}</strong> was added to{" "}
                {created.targetAccount?.email || destinationEmail} for{" "}
                {created.patient.date_of_appt}.
              </p>
              {integrated && (
                <div className="mt-5 rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm text-gray-900">
                  <p className="font-semibold">
                    Log into {integratedAccount.email} in Knowtex to access both
                    test patients.
                  </p>
                  <p className="mt-2">
                    Organization: Knowtex · Super organization: Knowtex ·
                    Specialty: Astera.
                  </p>
                </div>
              )}
              <dl className="mt-5 space-y-2 text-sm">
                <div>
                  <dt className="inline font-semibold">Status: </dt>
                  <dd className="inline">{created.patient.status}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold">Patient ID: </dt>
                  <dd className="inline break-all">
                    {created.patient.patient_id}
                  </dd>
                </div>
                {created.patient.appointment_id && (
                  <div>
                    <dt className="inline font-semibold">Appointment ID: </dt>
                    <dd className="inline break-all font-mono">
                      {created.patient.appointment_id}
                    </dd>
                  </div>
                )}
              </dl>
              {created.followUpPatient && (
                <div className="mt-5 rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm text-gray-700">
                  <p className="font-semibold text-gray-900">
                    Completed follow-up: {created.followUpPatient.description}
                  </p>
                  <p className="mt-2">
                    Patient ID: {created.followUpPatient.patient_id} · Status:{" "}
                    {created.followUpPatient.status} ·{" "}
                    {created.followUpPatient.date_of_appt}
                  </p>
                  <p className="mt-2 break-words">
                    Configured template:{" "}
                    <strong>
                      {created.followUpResources?.soapNoteTemplate}
                    </strong>{" "}
                    · Astera
                  </p>
                  <p className="mt-1 break-words">
                    Conversation prompt:{" "}
                    <strong>
                      {created.followUpResources?.conversationPrompt}
                    </strong>
                  </p>
                  <p className="mt-2">
                    IF YOU ARE DEMOING UPLOADING TO ONCOEMR BE SURE TO SET THE
                    PATIENT UP IN ONCO BY SCHEDULING AN APPOINTMENT AND DELETING
                    ANY PREVIOUSLY UPLOADED NOTES, CODES, AND ORDERS. Open this
                    follow-up in Review for the completed note, ICD-10 codes,
                    E&amp;M recommendation, orders, diarized transcript,
                    citations, chart history, and patient visit summary. Turn on
                    carry-forward highlights to show the prior assessment and
                    disease history, and use the suggested macros in the note
                    editor.
                  </p>
                </div>
              )}
              {created.clinicalTrial && (
                <div className="mt-5 rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm text-gray-700">
                  <p className="font-semibold text-gray-900">
                    Clinical eligibility screening: {created.clinicalTrial.code}
                  </p>
                  <p className="mt-2">
                    Open either patient in Knowtex, select{" "}
                    <strong>{created.clinicalTrial.code}</strong> in Clinical
                    Trials, then choose <strong>Check eligibility</strong>.
                  </p>
                  <p className="mt-2">
                    Review the Inclusion and Exclusion tabs, mark each criterion{" "}
                    <strong>Met</strong> or <strong>Not met</strong>, and save
                    your decisions. Criteria without enough information stay
                    unreviewed.
                  </p>
                </div>
              )}
              {created.specialtyUpdate && !integrated && (
                <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                  <p>
                    <strong>
                      Specialty updated to {created.specialtyUpdate.current}.
                    </strong>
                  </p>
                  <p className="mt-1">
                    Previous specialty:{" "}
                    <strong>
                      {created.specialtyUpdate.previous || "Not set"}
                    </strong>
                    .
                  </p>
                  <p className="mt-1">
                    When the demo is finished,{" "}
                    {created.specialtyUpdate.previous ? (
                      <>
                        return this user’s specialty to{" "}
                        <strong>{created.specialtyUpdate.previous}</strong>
                      </>
                    ) : (
                      "clear this user’s specialty to return it to its previous state"
                    )}
                    .
                  </p>
                </div>
              )}
              {created.ehrAccess && (
                <div className="mt-5 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-gray-700">
                  <p className="font-semibold text-gray-900">
                    {created.ehrAccess.superOrg} OncoEMR access
                  </p>
                  <p className="mt-2">
                    Impersonate into{" "}
                    <strong>{created.ehrAccess.impersonateEmail}</strong> in
                    Knowtex to access this test patient.
                  </p>
                  <dl className="mt-3 space-y-2">
                    <div>
                      <dt className="inline font-semibold">URL: </dt>
                      <dd className="inline">
                        <a
                          href={created.ehrAccess.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-ktx-purple underline"
                        >
                          {created.ehrAccess.url.replace(/^https?:\/\//, "")}
                        </a>
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-semibold">Username: </dt>
                      <dd className="inline select-all font-mono">
                        {created.ehrAccess.username}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-semibold">Password: </dt>
                      <dd className="inline select-all font-mono">
                        {created.ehrAccess.password}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
              {created.warning && (
                <p className="mt-4 text-sm text-amber-800">{created.warning}</p>
              )}
              <button
                type="button"
                onClick={reset}
                className="mt-6 rounded-lg bg-ktx-purple px-5 py-3 text-sm font-semibold text-white hover:bg-ktx-navy"
              >
                Add another test patient
              </button>
            </section>
          ) : (
            <form onSubmit={createPatient}>
              {integrated && answers.ehr === "OncoEMR" && (
                <div
                  role="note"
                  className="mb-5 rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm text-gray-900"
                >
                  <p className="font-semibold">
                    Both patients are always created for{" "}
                    {integratedAccount.email}.
                  </p>
                  <p className="mt-2">
                    Log into {integratedAccount.email} in Knowtex to access the
                    test patients. Each creation sets this account’s
                    organization and super organization to Knowtex and specialty
                    to Astera.
                  </p>
                </div>
              )}
              <div className="mb-5 flex items-center justify-between gap-4 text-sm">
                <p role="status" className="text-gray-500">
                  {complete
                    ? "All questions answered"
                    : `${completedCount} answered · ${visibleSteps.length === 1 ? "Start here" : "Keep going"}`}
                </p>
                {answers.purpose && (
                  <button
                    type="button"
                    onClick={reset}
                    disabled={creating}
                    className="font-semibold text-gray-500 hover:text-gray-900 disabled:opacity-50"
                  >
                    Start over
                  </button>
                )}
              </div>
              <fieldset disabled={creating} className="min-w-0 space-y-4">
                <legend className="sr-only">Test patient questions</legend>
                {visibleSteps.map((field, index) => (
                  <section
                    key={field}
                    aria-labelledby={`question-${field}`}
                    className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:p-6"
                  >
                    <div className="mb-4 flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${isAnswerComplete(field, answers[field], answers) ? "bg-purple-100 text-ktx-purple" : "bg-ktx-purple text-white"}`}
                      >
                        {isAnswerComplete(field, answers[field], answers) ? (
                          <Check size={16} />
                        ) : (
                          index + 1
                        )}
                      </span>
                      <h2
                        id={`question-${field}`}
                        className="pt-0.5 text-base font-semibold text-gray-900"
                      >
                        {field === "doctor" ? (
                          questions[field]
                        ) : (
                          <label htmlFor={`answer-${field}`}>
                            {questions[field]}
                          </label>
                        )}
                      </h2>
                    </div>
                    {renderField(field)}
                  </section>
                ))}
              </fieldset>
              {isUnavailableIntegratedEhr(answers) && (
                <p
                  role="alert"
                  className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                >
                  Integrated demos are unavailable for {answers.ehr}. Only
                  OncoEMR is available.
                </p>
              )}
              {complete && (
                <section
                  aria-label="Confirm test patient"
                  className="mt-6 rounded-xl border border-purple-200 bg-purple-50 p-6"
                >
                  <h2 className="font-semibold text-gray-900">Ready to add</h2>
                  <p className="mt-2 text-sm leading-6 text-gray-700">
                    Add {integrated ? "two" : "one"}{" "}
                    {answers.purpose === "sales"
                      ? `${destinationSpecialty} ${integrated ? `${answers.ehr} integrated` : "standalone"} sales demo`
                      : "onboarding"}{" "}
                    test {integrated ? "patients" : "patient"} to{" "}
                    <strong>{destinationEmail}</strong> on{" "}
                    <strong>{answers.date}</strong>.
                  </p>
                  <p className="mt-2 text-sm text-gray-600">
                    {integrated
                      ? "Creates a New patient Ready to Record and a separate Follow-up in Review with a completed note, coding, orders, transcript, citations, chart history, patient visit summary and suggested macros. This user’s specialty will be set to Astera."
                      : answers.purpose === "sales"
                        ? `The patient will use the New patient type and be Ready to Record with no note. This user’s specialty will be set to ${destinationSpecialty}.`
                        : answers.encounter === "review"
                          ? "The encounter will be in Review with the approved synthetic note."
                          : "The encounter will be Ready to Record with no note or transcript."}
                  </p>
                  {integrated && (
                    <p className="mt-2 break-words text-sm text-gray-600">
                      Follow-up template:{" "}
                      <strong>note_template_astera_default_fu</strong> · Astera
                      <br />
                      Conversation prompt:{" "}
                      <strong>conv_prompt_astera_Dr.Ronnen_fu</strong>
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={creating}
                    className="mt-5 rounded-lg bg-ktx-purple px-5 py-3 text-sm font-semibold text-white hover:bg-ktx-navy disabled:cursor-wait disabled:opacity-60"
                  >
                    {creating ? "Adding patient…" : "Add patient"}
                  </button>
                </section>
              )}
              {error && (
                <p
                  role="alert"
                  className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                >
                  {error}
                </p>
              )}
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
